import z, { boolean, literal, object, string, union } from 'zod';

import { serviceNowIdentifierSchema } from '../common/matchers';
import { ATLEAST_N, ATMOST_N } from '../common/messages';
import type { ApiWithStatusAndScopesDto } from './ApiDto';
import type { ScopeDto } from './ScopeDto';

export enum Decision {
  DENIED = 'DENIED',
  APPROVED = 'APPROVED',
  PENDING = 'PENDING',
  CANCELED = 'CANCELED',
  LEVEL1_APPROVED = 'LEVEL1_APPROVED',
  PRESUBMIT = 'PRESUBMIT',
}

export interface EntitlementRequestDto {
  id: string;
  appId: string;
  apiId: string;
  scopeIds: string[];
  decision: Decision;
  decisionComment?: string | null;
  decisionMadeAt?: string;
  useCase: string;
  createdAt: string;
  updatedAt: string;
  externalTicketComment?: string | null;
  externalTicketId?: string | null;
}

export interface EntitlementRequestWithScopeDto
  extends Pick<
    EntitlementRequestDto,
    | 'id'
    | 'decision'
    | 'apiId'
    | 'decisionComment'
    | 'decisionMadeAt'
    | 'useCase'
    | 'createdAt'
    | 'externalTicketComment'
    | 'externalTicketId'
  > {
  approvalStage?: string;
  apiName: string;
  apiDescription: string | null;
  scopes: ScopeDto[];
  testScopes: ScopeDto[];
  serviceNowIdentifier: string | null;
}

export interface AppApiEntitlementDto {
  apiId: string;
  liveMode: boolean;
  id: string;
}

export type CurrentApiAndScopeEntitlementsDto = {
  api: ApiWithStatusAndScopesDto;
  entitlements: AppApiEntitlementDto[];
  scopeEntitlements: ScopeEntitlement[];
};

export type ScopeEntitlement = {
  scopeId: string;
  scopeName: string;
  scopeDescription: string;
  liveMode: boolean;
};

export const appApiEntitlementRequestSchema = object({
  apiId: string().min(1),
  liveMode: boolean().default(false),
  useCase: string().max(500, ATMOST_N(500)),
  scopeIds: string().array(),
  termsOfUseAccepted: boolean().optional(),
  status: z.enum([Decision.PENDING, Decision.PRESUBMIT]).optional(),
});

export const editTestModeScopesRequestSchema = object({
  selectedScopeIds: string().array(),
});

export const appApiEntitlementRequestInputSchema = z.array(appApiEntitlementRequestSchema);

export type AppApiEntitlementRequest = z.infer<typeof appApiEntitlementRequestSchema>;

export type AppApiEntitlementInputRequest = z.infer<typeof appApiEntitlementRequestInputSchema>;

export const createApiAccessTicketVariablesInputRequestSchema = object({
  serviceNowIdentifier: serviceNowIdentifierSchema,
  serviceNowIdentifierSysId: string().min(3).max(255),
  consumerServiceOffering: string().optional(),
  onlyPersonsWithSpecificRoles: boolean().default(false), // only relevant if api classification is strictly-confidential
  dataWillBeEncryptedOnFieldLevel: boolean().default(false), // only relevant if api classification is strictly-confidential
  dataWillBeEncryptedOnTableLevel: boolean().default(false), // only relevant if api classification is strictly-confidential
  dataWillBeEncryptedInTransit: boolean().default(false), // only relevant if api classification is strictly-confidential
  subscriberType: union([literal('process'), literal('data'), literal('user_interface')], {
    required_error: 'Please select a subscriber type.',
  }),
  subscriberSubType: union(
    [
      // only relevant if subscriber type is data, else leave field empty
      literal('process_configuration'),
      literal('foundation_data'),
      literal('configuration_items'),
      literal('events'),
      literal('reporting'),
      literal('sys_log'),
    ],
    { required_error: 'Please select a subscriber sub-type.' },
  ).optional(),
  // only relevant if API exposes PII
  purpose: union([
    literal('Consent'),
    literal('Necessary to fulfill a contract'),
    literal('Necessary to fulfill a legal obligation'),
    literal('Necessary to protect vital interests'),
    literal('Necessary to perform a task in the public interest or exercise official authority'),
    literal('Necessary to pursue legitimate interests - balancing of interests'),
    literal('Other - explained in use case'),
  ]).optional(), // select the purpose you have for receiving the PII data
  subscriberDescription: string(), // the long description
});

const serviceNowMetaDataSchema = createApiAccessTicketVariablesInputRequestSchema
  .omit({
    subscriberDescription: true,
    serviceNowIdentifier: true,
    serviceNowIdentifierSysId: true,
  })
  .strict();

export const scopeObjectSchema = object({
  id: string(),
  name: string(),
  description: string(),
  test: boolean(),
  live: boolean(),
});

export const appApiEntitlementRequestSchemaV2 = object({
  useCase: string().max(500, ATMOST_N(500)),
  termsOfUseAccepted: boolean().optional(),
  status: z.enum([Decision.PENDING]).optional(),
  scopes: string().array(),
  testScopes: string().array(),
  serviceNowMetaData: serviceNowMetaDataSchema.optional(),
});

export type AppApiEntitlementRequestSchemaV2 = z.infer<typeof appApiEntitlementRequestSchemaV2>;

export const appApiEntitlementRequestSchemaV2Form = object({
  useCase: string().min(5, ATLEAST_N(5)).max(500, ATMOST_N(500)),
  termsOfUseAccepted: boolean().optional(),
  scopes: scopeObjectSchema.array().refine((scopes) => scopes.some((scope) => scope.test || scope.live), {
    message: 'Select at least one scope.',
  }),
  serviceNowMetaData: serviceNowMetaDataSchema
    .refine(
      (data) =>
        data.onlyPersonsWithSpecificRoles ||
        data.dataWillBeEncryptedOnFieldLevel ||
        data.dataWillBeEncryptedOnTableLevel ||
        data.dataWillBeEncryptedInTransit,
      {
        message: 'At least one of the data protection options must be enabled.',
      },
    )
    .optional(),
  requestType: z.enum(['edit', 'attach']),
}).refine(
  (data) => {
    if (data.requestType === 'attach') {
      return data.termsOfUseAccepted === true;
    }
    return true;
  },
  {
    message: 'Please accept terms and conditions.',
    path: ['termsOfUseAccepted'],
  },
);

export type AppApiEntitlementRequestSchemaV2Form = z.infer<typeof appApiEntitlementRequestSchemaV2Form>;

export interface CreateAppApiEntitlementsResponseDto {
  request?: EntitlementRequestDto;
  liveScopesRemoved: string[];
  testScopesRemoved: string[];
  testScopesAdded: string[];
}
